import React, { useState, useEffect, useCallback, useRef } from 'react';
import { commentsAPI } from '../../api';
import { showToast, showErrorToast } from '../Toast/toastUtils';
import CommentThreadList from './CommentThreadList';
import './CommentsPanel.css';

const PAGE_SIZE = 20;

const CommentsPanel = ({ isOpen, onClose, postId, postTitle, availablePlatforms = null, sourcePlatform = null, highlightItemId = null }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  // Infinite scroll state (cursor-based)
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  // Multi-select platform filter: array of platform names. null = all.
  const [selectedPlatforms, setSelectedPlatforms] = useState(null);

  // Determine which platforms are available for filtering.
  // If availablePlatforms is passed (from ComposeModal), use those. Otherwise derive from comments.
  const availablePlatformList = availablePlatforms && Array.isArray(availablePlatforms) && availablePlatforms.length > 0
    ? availablePlatforms
    : null;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && postId) {
      setComments([]);
      setCursor(null);
      setHasMore(true);
      setLoadingMore(false);

      // Set default filter based on sourcePlatform.
      if (sourcePlatform) {
        setSelectedPlatforms([sourcePlatform]);
      } else {
        setSelectedPlatforms(null); // null = all platforms
      }
    }
  }, [isOpen, postId, sourcePlatform]);

  // Scroll to and highlight the selected item when comments are loaded
  const highlightScrollRef = useRef(false);
  useEffect(() => {
    if (!highlightItemId || highlightScrollRef.current) return;
    if (comments.length > 0) {
      const el = document.querySelector(`[data-comment-id="${highlightItemId}"]`);
      if (el) {
        highlightScrollRef.current = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('highlight-flash');
        setTimeout(() => el.classList.remove('highlight-flash'), 2000);
      }
    }
  }, [comments.length, highlightItemId]);

  // Reset highlight ref when postId changes (new post opened)
  useEffect(() => {
    highlightScrollRef.current = false;
  }, [postId]);

  /**
   * Fetch top-level comments for this post (first page or append for infinite scroll).
   * When called from inbox (postId is a platform_post_id string), uses getByPlatformPostId.
   * When called from ComposeModal (postId is a DB int), uses getByPostId.
   * Uses cursor-based pagination.
   */
  const fetchCommentsPage = useCallback(async (pageCursor, append = false) => {
    if (!postId) return;
    if (!append) setLoading(true);
    try {
      // Determine which API to use based on postId type.
      // From inbox: postId is a platform_post_id (string, which may be purely numeric
      // for Facebook/Instagram provider IDs). We can't rely on "numeric = local DB ID"
      // because provider IDs are often numeric too.  Instead, when called from the
      // inbox we ALWAYS pass sourcePlatform (truthy), which is the signal to use
      // the platform-post endpoint.
      // From ComposeModal: postId is a DB Post ID (integer) and sourcePlatform is
      // not set.
      const isPlatformPostId = sourcePlatform != null;
      let data;
      if (isPlatformPostId) {
        data = await commentsAPI.getByPlatformPostId(postId, {
          platform: sourcePlatform,
          limit: PAGE_SIZE,
          cursor: pageCursor,
          highlightItemId: pageCursor ? null : highlightItemId,
        });
      } else {
        data = await commentsAPI.getByPostId(postId, {
          nested: false,
          limit: PAGE_SIZE,
          cursor: pageCursor,
          sync: false,
        });
      }
      const fetchedComments = data.comments || [];
      // Use backend's has_more if available, otherwise estimate
      setHasMore(data.has_more !== undefined ? data.has_more : fetchedComments.length === PAGE_SIZE);

      if (append) {
        setComments(prev => [...prev, ...fetchedComments]);
        // Update cursor for next page
        setCursor(data.next_cursor || null);
      } else {
        setComments(fetchedComments);
        // Set cursor for first page so infinite scroll can use it
        setCursor(data.next_cursor || null);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
      showErrorToast(`Failed to load comments: ${error.message || error}`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [postId, sourcePlatform, highlightItemId]);

  // Load first page when modal opens or filter changes.
  useEffect(() => {
    if (isOpen && postId) {
      fetchCommentsPage(null);
    }
  }, [isOpen, postId, selectedPlatforms, fetchCommentsPage]);

  /**
   * Infinite scroll: load next page when sentinel is visible.
   */
  useEffect(() => {
    if (!isOpen) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          setLoadingMore(true);
          fetchCommentsPage(cursor, true);
        }
      },
      { rootMargin: '200px' }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => observer.disconnect();
  }, [cursor, hasMore, loading, loadingMore, isOpen, fetchCommentsPage]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      setLoading(true);
      // Same logic as fetchCommentsPage - determine which API to use.
      // When called from the inbox we ALWAYS pass sourcePlatform (truthy).
      const isPlatformPostId = sourcePlatform != null;
      let data;
      if (isPlatformPostId) {
        data = await commentsAPI.getByPlatformPostId(postId, {
          platform: sourcePlatform,
          limit: PAGE_SIZE,
        });
      } else {
        data = await commentsAPI.getByPostId(postId, {
          nested: false,
          limit: PAGE_SIZE,
          sync: true,
        });
      }
      setComments(data.comments || []);
      setCursor(null);
      setHasMore(data.has_more !== undefined ? data.has_more : (data.comments || []).length === PAGE_SIZE);
      showToast('Comments synced successfully');
    } catch (error) {
      console.error('Error syncing comments:', error);
      showErrorToast(`Sync failed: ${error.message || error}`);
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  };

  // Callback passed to CommentThreadList — handles reply + refresh.
  const handleReply = useCallback(async (commentId, text) => {
    try {
      await commentsAPI.reply(commentId, text);
      await fetchCommentsPage(null);
      showToast('Reply posted successfully');
    } catch (error) {
      console.error('Error posting reply:', error);
      showErrorToast(`Failed to post reply: ${error.message || error}`);
      throw error;
    }
  }, [fetchCommentsPage]);

  // Callback passed to CommentThreadList — handles delete + refresh.
  const handleDelete = useCallback(async (commentId) => {
    try {
      const result = await commentsAPI.delete(commentId);
      await fetchCommentsPage(null);
      const body = result?.data ?? result;
      if (body && !body.platform_deleted) {
        const platformError = body.platform_error || 'unknown reason';
        showErrorToast(`Comment removed locally but could not be deleted from platform: ${platformError}`);
      } else {
        showToast('Comment deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      showErrorToast(`Failed to delete comment: ${error.message || error}`);
      throw error;
    }
  }, [fetchCommentsPage]);

  /**
   * Load replies for a comment (lazy-load nested replies).
   * Uses inboxAPI.getChildren on the comment's inbox item ID.
   * Uses cursor from comment._childrenCursor for continuation.
   */
  const handleLoadMoreReplies = useCallback(async (commentId) => {
    // Find the comment to get its cursor
    let targetCursor = null;
    const findCursor = (list) => {
      for (const c of list) {
        if (c.id === commentId) {
          targetCursor = c._childrenCursor || null;
          return true;
        }
        if (c.replies && c.replies.length > 0) {
          if (findCursor(c.replies)) return true;
        }
      }
      return false;
    };
    findCursor(comments);

    try {
      const data = await commentsAPI.getChildren(commentId, { cursor: targetCursor });
      // Update the comments tree to include the new replies
      setComments(prev => {
        const updated = updateCommentReplies(
          prev,
          commentId,
          data.items || [],
          !!data.has_more,
          data.next_cursor || null
        );
        return updated;
      });
    } catch (error) {
      console.error('Error loading replies:', error);
    }
  }, [comments]);

  /**
   * Inject loaded replies into nested comments array using O(n) Map — no recursive tree walk.
   */
  const updateCommentReplies = (commentsList, targetId, newReplies, hasMore, nextCursor) => {
    // Recursive clone-and-update. `commentsList` is the flat list of top-level
    // comments, but prior expansions nest their children in each node's
    // `.replies`. A flat byId map keyed only on top-level ids misses depth-2+
    // targets, so walk the tree recursively to find and extend the right node.
    const mapNode = (node) => {
      const cloned = { ...node, replies: (node.replies || []).map(mapNode) };
      if (cloned.id === targetId) {
        cloned.replies = [...cloned.replies, ...newReplies];
        cloned.has_more_children = hasMore;
        cloned._childrenCursor = nextCursor;
      }
      return cloned;
    };
    return commentsList.map(mapNode);
  };

  // Determine available platforms for checkboxes.
  const platformsList = availablePlatformList
    ? availablePlatformList
    : [...new Set(comments.map(c => c.platform).filter(Boolean))];

  if (!isOpen) return null;

  return (
    <div className="comments-overlay" onClick={onClose}>
      <div className="comments-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="comments-header">
          <div className="comments-title-section">
            <h2>Comments</h2>
            {postTitle && <p className="comments-post-title">{postTitle}</p>}
          </div>
          <div className="comments-header-actions">
            <button
              className={`sync-btn ${syncing ? 'syncing' : ''}`}
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? 'Syncing...' : '↻ Sync'}
            </button>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Platform filter checkboxes */}
        {platformsList.length > 0 && (
          <div className="comments-filters">
            <span className="filter-label">Filter by platform:</span>
            <div className="filter-checkboxes">
              <label className={`filter-checkbox-label ${(!selectedPlatforms || selectedPlatforms.length === 0) ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={!selectedPlatforms || selectedPlatforms.length === 0}
                  onChange={() => setSelectedPlatforms(null)}
                />
                <span>All</span>
              </label>
              {platformsList.map(p => (
                <label key={p} className={`filter-checkbox-label ${(selectedPlatforms || []).includes(p) ? 'active' : ''}`}>
                  <input
                    type="checkbox"
                    checked={(selectedPlatforms || []).includes(p)}
                    onChange={() => {
                      const current = selectedPlatforms || [];
                      if (current.includes(p)) {
                        const next = current.filter(pl => pl !== p);
                        setSelectedPlatforms(next.length === 0 ? null : next);
                      } else {
                        setSelectedPlatforms([...current, p]);
                      }
                    }}
                  />
                  <span style={{ textTransform: 'capitalize' }}>{p}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Comments list — delegated to CommentThreadList */}
        <div className="comments-list">
          {loading ? (
            <div className="comments-loading">Loading comments...</div>
          ) : (
            <CommentThreadList
              comments={comments}
              onReply={handleReply}
              onDelete={handleDelete}
              onLoadMoreReplies={handleLoadMoreReplies}
              selectedPlatforms={selectedPlatforms}
            />
          )}
          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="sentinel">
            {loadingMore && <div className="comments-loading">Loading more comments...</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentsPanel;
